import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, X, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const notForYou = [
  {
    title: "You're looking for a traditional agency",
    description: "We don't do marketing campaigns, branding packages, or SEO services. We help founders validate and build products.",
  },
  {
    title: "You want someone to just execute your spec",
    description: "We're thought partners, not order takers. If you have a fixed plan and just need hands to build it, there are better options.",
  },
  {
    title: "You need guaranteed outcomes",
    description: "Building startups is inherently uncertain. We help you reduce risk, but we can't promise results. Anyone who does is lying.",
  },
  {
    title: "You're optimizing for the cheapest option",
    description: "We're not the budget choice. We're priced for founders who understand that the wrong build costs more than the right one.",
  },
  {
    title: "You're not ready to hear hard truths",
    description: "We will tell you if your idea needs work. We'll challenge assumptions. That's how we help. If you want validation, we're not it.",
  },
  {
    title: "You want to scale before you've validated",
    description: "Scaling confusion is expensive. If you're not willing to start with validation, we're not the right fit.",
  },
];

const transparencyPoints = [
  "We take limited engagements, quality over quantity.",
  "We're honest about fit, sometimes that means saying no.",
  "We'd rather turn down work than do work that doesn't serve you.",
];

export default function WhoThisIsNotForPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero section-padding">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              Who This Is NOT For
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              We believe in clarity, which means being upfront about when we're not the right fit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Not For You List */}
      <section className="bg-background section-padding">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-4">We're Probably Not Right For You If...</h2>
            <p className="body-base text-muted-foreground">
              This isn't gatekeeping. It's respecting your time and ours.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {notForYou.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Card className="h-full border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                        <X className="h-4 w-4 text-destructive" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg font-medium text-foreground mb-2">{item.title}</h3>
                        <p className="text-muted-foreground text-sm">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're Transparent */}
      <section className="bg-section section-padding-sm">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-start gap-4 bg-card border border-border/50 rounded-xl p-8">
              <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-serif text-xl font-medium text-foreground mb-4">Why We're This Transparent</h3>
                <ul className="space-y-3">
                  {transparencyPoints.map((point, i) => (
                    <li key={i} className="text-muted-foreground">{point}</li>
                  ))}
                </ul>
                <p className="text-foreground mt-4 pt-4 border-t border-border/50">
                  If none of the above applies to you, there's a good chance we'd work well together.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground section-padding-sm">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-section mb-4">Still Here? Good.</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              If what you read resonates, we might be a great fit.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                <Link to="/contact">
                  Submit Your Wishlist
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/who-we-help">See Who We Help</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
